import { DeleteFilled } from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { GraviButton, Horizontal } from '@gravitate-js/excalibrr'
import { Col, Form, type FormInstance, InputNumber, Popover, Row, Select } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'

import { clearRowProduct } from '../../../Utils/general'
import { DropDisplay } from './DropDisplay'
import { TimezoneDateTimePicker } from '@/components/_SiteWide/TimezoneDateTimePicker'

const { Option } = Select

type DropRowProps = {
  index: number
  deleteRow: () => void
  editing: boolean
  stores: Gravitate.Site[]
  drop: Gravitate.StoreDrop
  form: FormInstance
  field: string
  fields: string[]
  name: string
  bolMetadata: Record<string, any>
  showWaterReading?: boolean
  formattedUnit?: string
  timezone: string
}

export const DropRow = ({
  index,
  deleteRow,
  editing,
  stores,
  drop,
  form,
  field,
  fields,
  name,
  bolMetadata,
  showWaterReading,
  formattedUnit,
  timezone,
}: DropRowProps) => {
  const [selectedTank, setSelectedTank] = useState({ id: drop?.tank_id, product: drop?.product })
  const [selectedSite, setSelectedSite] = useState()
  const { scopes } = useSecurityContext()
  const canDelete = fields?.length > 1
  const useInches = drop?.unit === 'in'
  const unit = useInches ? drop?.unit : formattedUnit?.toLowerCase()

  const allProducts = useMemo(() => {
    if (!selectedSite) {
      return [...new Set(bolMetadata?.products)]
    }
    const siteProductNames = selectedSite?.tanks?.map((t) => t?.product)
    const filteredProducts = bolMetadata?.products?.filter((p) => !siteProductNames.includes(p))
    return [...new Set(filteredProducts)]
  }, [bolMetadata?.products, selectedSite])

  useEffect(() => {
    if (stores && drop) {
      setSelectedSite(stores?.find((s) => s.store_number === drop?.store))
      const tankProduct = stores
        ?.find((s) => s.store_number === drop?.store)
        ?.tanks?.find((t) => t.tank_id === drop?.tank_id)?.product
      setSelectedTank({ id: drop?.tank_id, product: tankProduct })
    }
  }, [stores, drop])

  const productOptions = useMemo(() => {
    if (selectedSite?.tanks) {
      const uniqueProducts = [...new Set(selectedSite.tanks.map((t) => t?.product))]
      const siteProducts = {
        label: `At ${selectedSite?.store_number}`,
        options: uniqueProducts.map((t) => ({ value: t })),
      }
      const otherProducts = {
        label: 'All Products',
        options: allProducts?.map((p) => ({ value: p })),
      }
      return [siteProducts, otherProducts]
    }
    return []
  }, [selectedSite, allProducts])

  const tankProductOptions = useMemo(() => {
    if (!stores?.length) return []
    if (selectedSite) {
      return selectedSite.tanks?.map((t) => t)
    }
    if (drop) {
      return stores.find((s) => s.store_number === drop.store)?.tanks.map((tank) => tank)
    }
    return []
  }, [drop, stores, selectedSite])

  const onSiteSelect = (siteNumber: string) => {
    setSelectedSite(stores.find((s) => s.store_number === siteNumber))
    clearRowProduct(form, field, name)
    setSelectedTank(undefined)
    form
  }

  const onTankSelect = (value: string) => {
    const tank = selectedSite?.tanks?.find((t) => {
      return t.tank_id === value
    })
    if (!tank && !selectedSite) {
      setSelectedSite(stores.find((s) => s.store_number === drop?.store))
    } else {
      setSelectedTank({ id: tank?.tank_id, product: tank?.product })
    }
  }

  const storeWrapperRef = useRef(null)
  const tankIdWrapperRef = useRef(null)
  const productIdWrapperRef = useRef(null)

  const spanWidth = showWaterReading ? 2 : 3

  if (!editing)
    return (
      <DropDisplay
        drop={drop}
        index={index}
        selectedTank={selectedTank}
        spanWidth={spanWidth}
        showWaterReading={showWaterReading}
        unit={unit}
        useInches={useInches}
        timezone={timezone}
      />
    )
  return (
    <Row key={index} className='p-3 detail-row'>
      <Col align='left' span={3}>
        <div ref={storeWrapperRef}>
          <Form.Item
            name={[index, 'store']}
            rules={[{ required: true, message: 'Site is Required' }]}
            data-cy={`store${index}`}
          >
            <Select
              getPopupContainer={(_) => storeWrapperRef?.current}
              onSelect={onSiteSelect}
              style={{ width: '95%' }}
              showSearch
              placeholder='Search For Sites'
              disabled={scopes.is_carrier}
            >
              {stores?.map((s, i) => (
                <Option key={i} value={s.store_number}>
                  {s.store_number}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Col>
      <Col align='left' span={3}>
        <div ref={productIdWrapperRef}>
          <Form.Item
            name={[index, 'product']}
            rules={[{ required: true, message: 'Product is Required' }]}
            data-cy={`product${index}`}
          >
            <Select
              getPopupContainer={(_) => productIdWrapperRef?.current}
              style={{ width: '95%' }}
              showSearch
              placeholder='Search For Products'
              options={productOptions}
            />
          </Form.Item>
        </div>
      </Col>
      <Col align='left' span={3}>
        <div ref={tankIdWrapperRef}>
          <Form.Item
            name={[index, 'tank_id']}
            rules={[{ required: true, message: 'Tank is Required' }]}
            data-cy={`tank-id${index}`}
          >
            <Select
              getPopupContainer={(_) => tankIdWrapperRef?.current}
              onSelect={onTankSelect}
              style={{ width: '95%' }}
            >
              {tankProductOptions?.map((t, i) => (
                <Option key={i} value={t.tank_id}>
                  <Horizontal verticalCenter>
                    <b className='mr-1'>{t.tank_id}</b> ({t.product})
                  </Horizontal>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Col>
      <Col align='left' span={3}>
        <div>
          <Form.Item name={[index, 'date']} rules={[{ required: true, message: 'Date is Required' }]}>
            <TimezoneDateTimePicker picker='date' timezone={timezone} placeholder='Drop Date' />
          </Form.Item>
        </div>
      </Col>
      <Col align='left' span={2}>
        <div>
          <Form.Item
            name={[index, 'time']}
            rules={[{ required: true, message: 'Drop Time is Required' }]}
            data-cy='time'
          >
            <TimezoneDateTimePicker picker='time' timezone={timezone} format='HH:mm' placeholder='Drop Time' />
          </Form.Item>
        </div>
      </Col>
      <Col align='left' span={spanWidth}>
        {useInches ? (
          <Form.Item name={[index, 'start_inches']}>
            <InputNumber placeholder={`Start ${unit}`} style={{ width: '88%' }} addonAfter={unit} />
          </Form.Item>
        ) : (
          <Form.Item name={[index, 'start_volume']}>
            <InputNumber placeholder={`Start ${unit}`} style={{ width: '88%' }} addonAfter={unit} />
          </Form.Item>
        )}
      </Col>
      {showWaterReading && (
        <Col align='left' span={spanWidth}>
          <Form.Item name={[index, 'water_inches']} data-cy='water-inches'>
            <InputNumber placeholder='Water inches' style={{ width: '88%' }} addonAfter={'in'} />
          </Form.Item>
        </Col>
      )}
      <Col align='left' span={spanWidth}>
        {useInches ? (
          <Form.Item name={[index, 'end_inches']}>
            <InputNumber placeholder={`End ${unit}`} style={{ width: '88%' }} addonAfter={unit} />
          </Form.Item>
        ) : (
          <Form.Item name={[index, 'end_volume']}>
            <InputNumber placeholder={`End ${unit}`} style={{ width: '88%' }} addonAfter={unit} />
          </Form.Item>
        )}
      </Col>
      <Col align='left' span={spanWidth}>
        <Form.Item
          name={[index, 'quantity']}
          rules={[{ required: true, message: 'Drop Quantity is required' }]}
          data-cy='quantity'
        >
          <InputNumber placeholder='Drop Quantity' style={{ width: '80%' }} />
        </Form.Item>
      </Col>
      <Col span={1} align='right'>
        {editing && (
          <>
            {canDelete ? (
              <GraviButton
                className='delete-drop-button'
                icon={<DeleteFilled className='delete-drop' />}
                onClick={deleteRow}
              />
            ) : (
              <Popover content='You cannot delete the last drop on an order' placement='bottomRight'>
                <div>
                  <GraviButton
                    className='delete-drop-button'
                    icon={<DeleteFilled className='delete-drop' />}
                    onClick={deleteRow}
                    disabled
                  />
                </div>
              </Popover>
            )}
          </>
        )}
      </Col>
    </Row>
  )
}
